import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../../config";
import { getAuthHeaders } from "../../services/useAuth";
import CustomButton from "../../pages/CustomButton";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Loader from "../../dist/Loader";
import formatDate from "../../utils/formatDate";

// Purple circled checkmark (reintroduced)
const PurpleCircleCheck = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle
      cx="12"
      cy="12"
      r="10.2"
      fill="#fff"
      stroke="#7b3fb2"
      strokeWidth="1.6"
    />
    <path
      d="M7.2 12.4l2.4 2.4L16.1 8"
      stroke="#6b2390"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EmployeeExpenseTable = () => {
  const navigate = useNavigate();
  const now = new Date();
  const tableRef = useRef(null);

  // Filters state
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [employeesForSelect, setEmployeesForSelect] = useState([
    { id: "All", first_name: "All", last_name: "" },
  ]);

  const [selectedState, setSelectedState] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("All");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedYear, setSelectedYear] = useState(String(now.getFullYear()));
  const [selectedMonth, setSelectedMonth] = useState(
    String(now.getMonth() + 1).padStart(2, "0"),
  );

  // table / UI
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subStates, setSubStates] = useState([]);
  const [selectedSubState, setSelectedSubState] = useState("");

  // tracks whether user clicked Go (so table is hidden before first search)
  const [hasSearched, setHasSearched] = useState(false);

  // static month names and years
  const months = [
    { name: "Jan", value: "01" },
    { name: "Feb", value: "02" },
    { name: "Mar", value: "03" },
    { name: "Apr", value: "04" },
    { name: "May", value: "05" },
    { name: "Jun", value: "06" },
    { name: "Jul", value: "07" },
    { name: "Aug", value: "08" },
    { name: "Sep", value: "09" },
    { name: "Oct", value: "10" },
    { name: "Nov", value: "11" },
    { name: "Dec", value: "12" },
  ];
  const years = Array.from({ length: 6 }, (_, i) =>
    String(now.getFullYear() - i),
  );
  const fetchSubStates = async (stateId) => {
    if (!stateId) {
      setSubStates([]);
      setSelectedSubState("");
      return;
    }

    try {
      const res = await axios.get(`${BASE_URL}/api/substates`, {
        params: { state_id: stateId },
        headers: getAuthHeaders(),
      });

      setSubStates(res.data || []);
      setSelectedSubState(""); // reset on state change
    } catch (err) {
      console.error("Failed to fetch sub states", err);
      setSubStates([]);
    }
  };

  // load states once
  useEffect(() => {
    let cancelled = false;
    const fetchStates = async () => {
      try {
        const r = await axios.get(`${BASE_URL}/api/states`, {
          headers: getAuthHeaders(),
        });
        if (!cancelled)
          setStates(Array.isArray(r.data) ? r.data : r.data?.data || []);
      } catch (e) {
        console.error("Failed to load states", e);
        if (!cancelled) setStates([]);
      }
    };
    fetchStates();
    return () => (cancelled = true);
  }, []);

  // load divisions once (fallback to reasonable default)
  useEffect(() => {
    let cancelled = false;
    const fetchDivisions = async () => {
      try {
        const r = await axios.get(`${BASE_URL}/api/divisions`, {
          headers: getAuthHeaders(),
        });
        if (!cancelled)
          setDivisions(Array.isArray(r.data) ? r.data : r.data?.data || []);
      } catch (e) {
        console.error("Failed to load divisions", e);
        if (!cancelled) {
          setDivisions([
            { id: "Cosmacia", name: "Cosmacia" },
            { id: "Other", name: "Other Division" },
          ]);
        }
      }
    };
    fetchDivisions();
    return () => (cancelled = true);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchEmployeesForSelect = async () => {
      if (!selectedState || !selectedSubState) {
        setEmployeesForSelect([
          { id: "All", first_name: "All", last_name: "" },
        ]);
        setSelectedEmployee("All");
        return;
      }

      try {
        const params = {
          state_id: selectedState,
          sub_state_id: selectedSubState,
        };

        if (selectedDivision && selectedDivision !== "All") {
          params.division_id = String(selectedDivision).trim();
        }

        const r = await axios.get(`${BASE_URL}/api/employees`, {
          params,
          headers: getAuthHeaders(),
        });

        // ✅ DEFINE LIST HERE
        const list = Array.isArray(r.data) ? r.data : r.data?.data || [];

        setEmployeesForSelect([
          { id: "All", first_name: "All", last_name: "" },
          ...list.map((e) => ({
            id: e.id ?? e.employee_id,
            first_name: e.first_name ?? "",
            last_name: e.last_name ?? "",
            employee: e.employee ?? e.emp_code ?? "",
            designation: e.designation ?? "",
            division: e.division ?? "",
            state_name: e.state_name ?? e.state ?? "",
          })),
        ]);

        setSelectedEmployee("All");
      } catch (e) {
        console.error("Employee fetch failed", e);
        setEmployeesForSelect([
          { id: "All", first_name: "All", last_name: "" },
        ]);
      }
    };

    fetchEmployeesForSelect();
    return () => (cancelled = true);
  }, [selectedState, selectedSubState, selectedDivision]);

  const handleGoClick = useCallback(async () => {
    setHasSearched(true);

    if (!selectedState) {
      setError("Please select a state");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const monthName =
        months.find((m) => m.value === selectedMonth)?.name || selectedMonth;

      const params = {
        state_id: selectedState || undefined,
        sub_state_id: selectedSubState || undefined,
        month_year: `${monthName} ${selectedYear}`,
      };

      // Safe employee
      if (selectedEmployee && selectedEmployee !== "All") {
        params.employee_id = String(selectedEmployee).trim();
      }

      // Safe division
      if (selectedDivision && selectedDivision !== "All") {
        params.division_id = String(selectedDivision).trim();
      }

      if (selectedDivision && selectedDivision !== "All") {
        params.division_id = String(selectedDivision).trim();
      }

      const url = `${BASE_URL}/api/employee-expense-report`;
      const res = await axios.get(url, {
        params,
        headers: getAuthHeaders(),
      });

      const payload = Array.isArray(res.data) ? res.data : res.data?.data || [];

      const normalizedRows = payload.map((p, idx) => ({
        sno: idx + 1,
        emp_id: p.emp_id,
        empid: p.employee,
        state: p.state,
        subState: p.sub_state,
        employee: p.employee_name, // ✅ Name
        division: p.division,
        designation: p.designation,
        city: p.city,
        month: p.month,
        approvedBy: p.approved_by_month ? formatDate(p.approved_by_month) : "",
        hrRemarks: p.hr_remarks,
        isApprovedByHr: p.is_approved_by_hr ?? false,
        daysFilled: p.days_filled ?? 0,
        dailyApproved: p.daily_approved ?? 0,
        latestDayExpense: p.latest_day_expense
          ? formatDate(p.latest_day_expense)
          : "",
        manager: p.manager,
        managerRemarks: p.manager_remarks,
        isApproved: p.is_approved ?? false,
      }));

      setData(normalizedRows);
    } catch (e) {
      console.error("Failed to fetch employee expense report", e);
      setData([]);

      if (e.response?.data?.message) {
        setError("API: " + e.response.data.message);
      } else {
        setError(
          "Failed to fetch employee expense report: " +
            (e.message || "Unknown error"),
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedState,
    selectedDivision,
    selectedYear,
    selectedMonth,
    months,
    selectedEmployee,
    selectedSubState,
  ]);

  // Handlers
  const handleView = (row) => {
    const url = `/admin/expense/expense-report-view/${row.emp_id}/${row.month}`;
    const newWindow = window.open(url, "", "width=800,height=600");
    if (newWindow) {
      newWindow.focus();
    }
  };

  const exportToExcel = () => {
    if (!data || data.length === 0) return;
    const exportData = data.map((r) => ({
      "S.No": r.sno,
      "Emp ID": r.employee,
      Employee: r.employee,
      State: r.state,
      "Sub State": r.subState,
      Division: r.division,
      Designation: r.designation,
      City: r.city,
      Month: r.month,
      "Approved By Month": r.approvedBy,
      "HR Remarks": r.hrRemarks,
      "Days Filled": r.daysFilled,
      "Daily Approved": r.dailyApproved,
      "Latest Day Expense": r.latestDayExpense,
      Manager: r.manager,
      "Manager Remarks": r.managerRemarks,
      Approved: r.isApproved ? "Yes" : "No",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Expense Report");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const dataBlob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const stateName = selectedState
      ? states.find((s) => String(s.id) === String(selectedState))?.name ||
        selectedState
      : "AllStates";

    const monthName =
      months.find((m) => m.value === selectedMonth)?.name || selectedMonth;

    const fileName = `Expense_Report_${
      selectedDivision || "Division"
    }_${stateName}_${monthName}_${selectedYear}.xlsx`;
    saveAs(dataBlob, fileName);
  };

  // Print only the table content (uses tableRef). Keeps your print styling.
  const handlePrint = () => {
    if (!tableRef?.current) return;

    const tableHtml = tableRef.current.innerHTML;
    const stateName = selectedState
      ? states.find((s) => String(s.id) === String(selectedState))?.name ||
        selectedState
      : "All";
    const monthName =
      months.find((m) => m.value === selectedMonth)?.name || selectedMonth;

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      console.error("Unable to open print window");
      return;
    }

    printWindow.document.write(`
    <html>
      <head>
        <title>Expense Report</title>
        <style>
          @page { 
            size: landscape; 
            margin: 10mm;
          }
          body { 
            font-family: Arial, sans-serif; 
            margin: 0; 
            padding: 20px; 
          }
          .print-header {
            margin-bottom: 15px;
          }
          .print-title {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 5px;
          }
          .print-filters {
            font-size: 14px;
            margin-bottom: 15px;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 20px; 
            font-size: 10px;
          }
          th, td { 
            border: 1px solid #ddd; 
            padding: 6px 8px; 
            text-align: left; 
          }
          th { 
            background-color: #f2f2f2; 
            font-weight: bold;
          }
          tr { 
            page-break-inside: avoid; 
          }
          /* keep SVG checkmark visible and sized for print */
          svg { vertical-align: middle; }
        </style>
      </head>
      <body>
        <div class="print-header">
          <div class="print-title">Expense Report</div>
          <div class="print-filters">
            <div>Division: ${selectedDivision || "All"}</div>
            <div>State: ${stateName}</div>
            <div>Month: ${monthName} ${selectedYear}</div>
            <div>Generated on: ${new Date().toLocaleString()}</div>
          </div>
        </div>
        <div class="table-container">
          ${tableHtml}
        </div>
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              window.close();
            }, 300);
          }
        </script>
      </body>
    </html>
  `);

    printWindow.document.close();
  };

  return (
    <div className="p-2  border-2 border-gray-300 rounded-lg bg-white">
      {/* Filters */}

      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <Loader onCancel={() => setIsLoading(false)} />
        </div>
      )}
      <div className="p-2 border-2 border-gray-300 rounded-xl bg-white shadow-md">
        {/* Title */}
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-6">
          <span className="text-blue-700">Expense </span>
          <span className="text-blue-500">Approved</span>
        </h1>

        <form className="w-full" onSubmit={(e) => e.preventDefault()}>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
            {/* LEFT SECTION */}
            <div className="md:col-span-5 col-span-12 space-y-4">
              {/* State */}
              <div className="flex items-center">
                <label className="w-24 text-right mr-4 text-sm text-gray-700">
                  State <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedState}
                  onChange={(e) => {
                    const stateId = e.target.value;
                    setSelectedState(stateId);

                    // reset downstream
                    setSelectedSubState("");
                    setSelectedEmployee("All");

                    fetchSubStates(stateId);
                  }}
                  disabled={isLoading}
                  className="flex-1 border-2 border-gray-300 rounded px-2 text-sm h-9 bg-white 
              focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition"
                >
                  <option value="">Select State</option>
                  {states.map((s) => (
                    <option key={s.id ?? s.state_id} value={s.id ?? s.state_id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Division */}
              <div className="flex items-center">
                <label className="w-24 text-right mr-4 text-sm text-gray-700">
                  Division
                </label>
                <select
                  value={selectedDivision}
                  onChange={(e) => setSelectedDivision(e.target.value)}
                  className="flex-1 border-2 border-gray-300 rounded px-2 text-sm h-9 bg-white 
              focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition"
                >
                  <option value="All">All</option>
                  {divisions.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Year */}
              <div className="flex items-center">
                <label className="w-24 text-right mr-4 text-sm text-gray-700">
                  Year
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="flex-1 border-2 border-gray-300 rounded px-2 text-sm h-9 bg-white 
              focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition"
                >
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* SPACER */}
            <div className="md:col-span-2 col-span-12"></div>

            {/* RIGHT SECTION */}

            <div className="md:col-span-5 col-span-12 space-y-4">
              <div className="flex items-center">
                <label className="w-24 text-right mr-4 text-sm text-gray-700">
                  Sub State <span className="text-red-500">*</span>
                </label>

                <select
                  value={selectedSubState}
                  onChange={(e) => {
                    setSelectedSubState(e.target.value);
                    setSelectedEmployee("All"); // reset employee
                  }}
                  disabled={!selectedState || isLoading}
                  className="flex-1 border-2 border-gray-300 rounded px-2 text-sm h-9 bg-white 
                    focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition disabled:bg-gray-100"
                >
                  <option value="">Select Sub State</option>
                  {subStates.map((ss) => (
                    <option
                      key={ss.id ?? ss.sub_state_id}
                      value={ss.id ?? ss.sub_state_id}
                    >
                      {ss.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Employee */}
              <div className="flex items-center">
                <label className="w-24 text-right mr-4 text-sm text-gray-700">
                  Employee
                </label>
                <select
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  disabled={!selectedState || isLoading}
                  className="flex-1 border-2 border-gray-300 rounded px-2 text-sm h-9 bg-white 
              focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition disabled:bg-gray-100"
                >
                  {employeesForSelect.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.id === "All"
                        ? "All"
                        : `${emp.first_name ?? ""} ${emp.last_name ?? ""}  ${emp.employee ?? ""} | ${emp.designation || "-"} | ${emp.division || "-"} | ${emp.state_name || "-"}`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Month */}
              <div className="flex items-center">
                <label className="w-24 text-right mr-4 text-sm text-gray-700">
                  Month
                </label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="flex-1 border-2 border-gray-300 rounded px-2 text-sm h-9 bg-white 
              focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition"
                >
                  {months.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Extra spacing match */}
              <div className="h-6"></div>
            </div>

            {/* BUTTON ROW */}
            <div className="md:col-span-12 col-span-12 flex justify-center mt-4">
              <CustomButton
                type="button"
                onClick={handleGoClick}
                variant="save"
                className="px-10"
                disabled={isLoading}
              >
                GO
              </CustomButton>
            </div>
          </div>

          {error && (
            <p className="text-center text-sm text-red-600 mt-3">⚠ {error}</p>
          )}
        </form>
      </div>
      {!hasSearched && (
        <div className="text-center py-8 text-sm text-gray-500">
          Use filters above and click <span className="font-semibold">Go</span>{" "}
          to fetch employee expenses.
        </div>
      )}

      {/* Show message when searched but no records */}
      {hasSearched && data.length === 0 && !isLoading && (
        <div className="text-center py-6 text-sm text-gray-600 bg-white rounded-md shadow p-4">
          No records found for the selected filters.
        </div>
      )}

      {/* Show table only when there is data */}
      {hasSearched && data.length > 0 && (
        <>
          <div
            ref={tableRef}
            className="mt-5 border rounded shadow overflow-visible"
          >
            <table className="w-full table-auto text-sm break-words">
              <thead className="bg-blue-500 text-white">
                <tr>
                  {[
                    "S.No",
                    "Emp ID",
                    "State",
                    "Sub State",
                    "Employee Name",
                    "Division",
                    "Designation",
                    "City",
                    "Month",
                    "Approved Month",
                    "HR Remarks",
                    "HR Approved",
                    "Days Filled",
                    "Daily Approved",
                    "Latest Expense",
                    "Manager",
                    "Manager Remarks",
                    "Approved",
                    "View",
                    "SMS",
                  ].map((h) => (
                    <th key={h} className="border px-2 py-1 whitespace-normal">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {data.map((row) => {
                  const approved =
                    row.isApproved ||
                    String(row.managerRemarks || "")
                      .toLowerCase()
                      .includes("approved");

                  return (
                    <tr key={row.sno}>
                      <td className="border px-2 py-1 text-center">
                        {row.sno}
                      </td>
                      <td className="border px-2 py-1">{row.empid}</td>
                      <td className="border px-2 py-1">{row.state}</td>
                      <td className="border px-2 py-1">{row.subState}</td>
                      <td className="border px-2 py-1">{row.employee}</td>
                      <td className="border px-2 py-1">{row.division}</td>
                      <td className="border px-2 py-1">{row.designation}</td>
                      <td className="border px-2 py-1">{row.city}</td>
                      <td className="border px-2 py-1">{row.month}</td>
                      <td className="border px-2 py-1">{row.approvedBy}</td>
                      <td className="border px-2 py-1">{row.hrRemarks}</td>
                      <td className="border px-2 py-1 text-center">
                        {row.isApprovedByHr ? "Yes" : "No"}
                      </td>
                      <td
                        className="border px-2 py-1 text-center font-semibold"
                        style={{ background: "#a7f0d9", color: "#006d46" }}
                      >
                        {row.daysFilled}
                      </td>

                      <td
                        className="border px-2 py-1 text-center font-semibold"
                        style={{ background: "#a7f0d9", color: "#006d46" }}
                      >
                        {row.dailyApproved}
                      </td>
                      <td className="border px-2 py-1">
                        {row.latestDayExpense}
                      </td>
                      <td className="border px-2 py-1">{row.manager}</td>
                      <td className="border px-2 py-1">{row.managerRemarks}</td>
                      <td className="border px-2 py-1 text-center">
                        {approved ? <PurpleCircleCheck /> : null}
                      </td>
                      <td className="border px-2 py-1 text-center">
                        <button
                          onClick={() => handleView(row)}
                          className="px-3 py-1 rounded-md text-[12px] font-medium border border-gray-300 hover:shadow-sm"
                        >
                          VIEW
                        </button>
                      </td>
                      <td className="border px-2 py-1 text-center">
                        <button>SMS</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer actions - visible only when data is present */}
          <div className="mt-4 flex gap-4 justify-center">
            <CustomButton onClick={handlePrint} label="Print" variant="print">
              🖨
            </CustomButton>
            <CustomButton
              onClick={exportToExcel}
              label="Export"
              variant="export"
            >
              📥
            </CustomButton>
          </div>
        </>
      )}
    </div>
  );
};

export default EmployeeExpenseTable;
